import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePageSize from "@/hooks/usePageSize";
import useSearchFilters from "@/hooks/useSearchFilters";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import { FilterBool, FilterSelect } from "@/UI/inputs/FilterSelect";
import FilterRange from "@/UI/inputs/FilterRange";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

interface Feedback {
    _id: string;
    customer?: {
        _id: string;
        name: string;
        phone: string;
        picture?: string;
    };
    product?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        picture?: string;
        price?: number;
    };
    branch?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        address?: string | { lang: string; value: string }[];
    };
    order?: {
        _id: string;
        number: string;
        totalPrice: number;
        status: string;
        date: string;
    };
    rate: number;
    feedback?: string;
    hidden: boolean;
    createdAt: string;
}

export default function FeedbacksScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const [page, setPage] = useState(1);
    const { pageSize, setPageSize } = usePageSize();

    const filters = useSearchFilters({
        search: undefined as string | undefined,
        min_rate: undefined as string | undefined,
        max_rate: undefined as string | undefined,
        hidden: [] as string[],
        branch: [] as string[],
        product: [] as string[],
        date_from: undefined as string | undefined,
        date_to: undefined as string | undefined,
    });

    // ── Fields fetch ────────────────────────────────────────────────────────────
    const { data: fieldsData } = useFetch("feedbacks/fields");
    const branchOptions: { _id: string; name: string }[] = fieldsData?.data?.branches ?? [];
    const productOptions: { _id: string; name: string }[] = fieldsData?.data?.products ?? [];

    // ── Feedbacks fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("feedbacks", { query: { page: 1, limit: pageSize, ...filters.committedQuery } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...filters.committedQuery } });
    }, [page]);

    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const total: number = data?.data?.total ?? 0;

    useEffect(() => {
        setFeedbacks(data?.data?.feedbacks ?? []);
    }, [data]);

    // ── Hide toggle fetch ───────────────────────────────────────────────────────
    const [hidingId, setHidingId] = useState<string | null>(null);
    const hidePrevRef = useRef<Feedback | null>(null);
    const { loading: hiding, status: hideStatus, invoke: hideInvoke } = useFetch("feedbacks", { method: "PUT" }, false);

    const wasHiding = useRef(false);
    useEffect(() => {
        if (hiding) {
            wasHiding.current = true;
        } else if (wasHiding.current) {
            wasHiding.current = false;
            if (hideStatus !== 200 && hidePrevRef.current) {
                const prev = hidePrevRef.current;
                setFeedbacks((prevList) => prevList.map((f) => (f._id === prev._id ? prev : f)));
            }
            setHidingId(null);
            hidePrevRef.current = null;
        }
    }, [hiding]);

    const handleToggleHide = (feedback: Feedback) => {
        if (hidingId) return;
        hidePrevRef.current = feedback;
        setHidingId(feedback._id);
        setFeedbacks((prev) => prev.map((f) => (f._id === feedback._id ? { ...f, hidden: !f.hidden } : f)));
        hideInvoke({ param: `${feedback._id}/hide` });
    };

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...filters.committedQuery } });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const getLocalizedValue = (value: string | { lang: string; value: string }[] | undefined): string => {
        if (!value) return "—";
        if (typeof value === "string") return value;
        if (Array.isArray(value)) {
            const match = value.find((v) => v.lang === lang) ?? value[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Feedback>[] = [
        {
            key: "customer",
            header: t.feedbacks.col.customer,
            width: "160px",
            render: (row) => (
                <div className="feedbacks-customer">
                    {row.customer?.picture ? (
                        <img src={row.customer.picture} alt={row.customer.name} className="feedbacks-customer-picture" />
                    ) : (
                        <div className="feedbacks-customer-placeholder">{row.customer?.name?.charAt(0) ?? "?"}</div>
                    )}
                    <TableCell.Text value={row.customer?.name || "—"} maxChars={18} />
                </div>
            ),
        },
        {
            key: "order",
            header: t.feedbacks.col.order,
            width: "110px",
            render: (row) => (row.order?.number ? <span className="feedbacks-order-number">#{row.order.number}</span> : "—"),
        },
        {
            key: "product",
            header: t.feedbacks.col.product,
            width: "140px",
            render: (row) => <TableCell.Text value={getLocalizedValue(row.product?.name)} maxChars={18} />,
        },
        {
            key: "branch",
            header: t.feedbacks.col.branch,
            width: "130px",
            render: (row) => <TableCell.Text value={getLocalizedValue(row.branch?.name)} maxChars={16} />,
        },
        {
            key: "rate",
            header: t.feedbacks.col.rate,
            width: "130px",
            render: (row) => (
                <div className="feedbacks-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <img
                            key={star}
                            src={star <= row.rate ? "/icons/star-active-icon.svg" : "/icons/star-inactive-icon.svg"}
                            alt=""
                            className="feedbacks-star-icon"
                        />
                    ))}
                </div>
            ),
        },
        {
            key: "feedback",
            header: t.feedbacks.col.feedback,
            width: "220px",
            render: (row) =>
                row.hidden ? (
                    <div className="feedbacks-hidden-text">
                        <img src="/icons/eye-closed-icon.svg" alt="" className="feedbacks-hidden-icon" />
                        <span className="feedbacks-masked">{t.feedbacks.hiddenText}</span>
                    </div>
                ) : (
                    <TableCell.Text value={row.feedback || "—"} maxChars={35} />
                ),
        },
        {
            key: "hidden",
            header: t.feedbacks.col.hidden,
            width: "90px",
            render: (row) =>
                row.hidden ? (
                    <TableCell.Badge label={t.feedbacks.col.hidden} variant="gray" />
                ) : (
                    <TableCell.Badge label={t.feedbacks.col.visible} variant="orange" />
                ),
        },
        {
            key: "date",
            header: t.feedbacks.col.date,
            width: "120px",
            render: (row) => <TableCell.Text value={formatDate(row.createdAt)} />,
        },
        {
            key: "actions",
            header: t.feedbacks.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={hidingId === row._id}
                    actions={[
                        {
                            name: row.hidden ? "unhide" : "hide",
                            icon: row.hidden ? "/icons/eye-opened-icon.svg" : "/icons/eye-closed-icon.svg",
                            handler: () => handleToggleHide(row),
                        },
                    ]}
                />
            ),
        },
    ];

    const filterContent = (
        <>
            <FilterSearchInput
                value={filters.draft.search}
                onChange={(v) => filters.setDraftField("search", v)}
                placeholder={t.filters.feedbacks.search}
            />
            <FilterRange
                label={t.filters.feedbacks.rateRange}
                fromValue={filters.draft.min_rate}
                toValue={filters.draft.max_rate}
                onFromChange={(v) => filters.setDraftField("min_rate", v)}
                onToChange={(v) => filters.setDraftField("max_rate", v)}
                fromPlaceholder="1"
                toPlaceholder="5"
            />
            <FilterBool
                label={t.filters.feedbacks.hidden}
                value={filters.draft.hidden as string[]}
                onChange={(v) => filters.setDraftField("hidden", v)}
                yesLabel={t.feedbacks.col.hidden}
                noLabel={t.feedbacks.col.visible}
            />
            {branchOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.feedbacks.branch}
                    options={branchOptions.map((o) => ({ value: o._id, label: o.name }))}
                    value={filters.draft.branch as string[]}
                    onChange={(v) => filters.setDraftField("branch", v)}
                />
            )}
            {productOptions.length > 0 && (
                <FilterSelect
                    label={t.filters.feedbacks.product}
                    options={productOptions.map((o) => ({ value: o._id, label: o.name }))}
                    value={filters.draft.product as string[]}
                    onChange={(v) => filters.setDraftField("product", v)}
                />
            )}
            <FilterRange
                label={t.filters.feedbacks.dateRange}
                fromValue={filters.draft.date_from}
                toValue={filters.draft.date_to}
                onFromChange={(v) => filters.setDraftField("date_from", v)}
                onToChange={(v) => filters.setDraftField("date_to", v)}
                type="date"
            />
        </>
    );

    return (
        <div className="feedbacks-screen">
            <ActionsBar
                filterContent={filterContent}
                filterOpen={filters.filterOpen}
                onFilterOpen={filters.openFilter}
                onFilterSave={() =>
                    filters.saveFilter((q) => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize, ...q } });
                    })
                }
                onFilterClear={() =>
                    filters.clearFilter(() => {
                        setPage(1);
                        invoke({ query: { page: 1, limit: pageSize } });
                    })
                }
                onFilterCancel={filters.cancelFilter}
                hasActiveFilters={filters.hasActiveFilters}
            />
            <div className="feedbacks-screen-content">
                <Table
                    columns={columns}
                    data={feedbacks}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
                />
            </div>
        </div>
    );
}
